import { BackendError, Service } from '@genial/services';
import { ResponsesOptoutChannelByCustomerIdResponse } from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { advisorIdToApisSelector, partnerIdToApisSelector } from '../master/selector';

import { orderItemState, orderStatusState } from './atom';
import { screenName } from './constants';
import { normalizeInteractionChannels } from './normalize';
import { ChannelsAvailable, OrderData, OrderStatus } from './types';

export const changeOrderStatus = selector({
  key: 'changeOrderStatus',
  set: ({ set, get }, newStatus) => {
    const currentOrderData = get(orderStatusState);
    const data = { ...currentOrderData, status: newStatus } as OrderStatus;
    set(orderStatusState, data);
  },
  get: ({ get }) => {
    const currentStatus = get(orderStatusState);
    return currentStatus.status;
  },
});

export const hasOrderItem = selector({
  key: 'hasOrderItem',
  get: ({ get }) => {
    const order = get(orderItemState);
    return !!(order && order?.name);
  },
});

export const selectOrderChannels = selector({
  key: 'selectOrderChannels',
  set: ({ set, get }, newValue) => {
    const order = get(orderItemState);
    const updatedOrder = { ...order, channels: newValue } as OrderData;
    set(orderItemState, updatedOrder);
  },
  get: ({ get }) => {
    const order = get(orderItemState);
    return order.channels;
  },
});

export const orderItem = selector({
  key: 'orderItem',
  set: ({ set, get }, newValue) => {
    const order = get(orderItemState);
    const value = newValue as OrderData;
    const data = { ...order, ...value } as OrderData;
    set(orderItemState, data);
  },
  get: ({ get }) => {
    const item = get(orderItemState);
    return item;
  },
});

export const orderInteractionChannelAuditRequest = selector<OrderData['interactionChannelAudit']>({
  key: 'orderInteractionChannelAuditRequest',
  set: ({ set, get }, newValue) => {
    const order = get(orderItemState);
    const updatedOrder = { ...order, interactionChannelAudit: newValue } as OrderData;
    set(orderItemState, updatedOrder);
  },
  get: ({ get }) => {
    const order = get(orderItemState);
    return order.interactionChannelAudit;
  },
});

export const hasOrderChannelsSelector = selector({
  key: 'hasOrderChannelsSelector',
  get: ({ get }) => {
    const order = get(orderItemState);
    return !!(order && order?.channels && order?.channels.length > 0);
  },
});

const normalizeAvailableChannels = ({
  channels,
}: ResponsesOptoutChannelByCustomerIdResponse): ChannelsAvailable => {
  return {
    whatsapp: !channels?.includes('WHATSAPP'),
  };
};

export const orderOptoutChannelsQuery = selectorFamily<
  { data: ChannelsAvailable | null } | null | undefined,
  { customerId?: number }
>({
  key: 'Order/OptoutChannels',
  get:
    ({ customerId }) =>
    async ({ get }) => {
      if (!customerId) {
        return null;
      }

      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      try {
        const { data } = await Service.Customer.V1.getOptoutByChannelCustomerId({
          customerId,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
        });

        const availableChannels = normalizeAvailableChannels(data);

        return { data: availableChannels };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: normalizeAvailableChannels({ channels: [] }) };
        }

        throw error;
      }
    },
});

export const orderInteractionChannelAuditQuery = selector({
  key: 'Order/InteractionChannelAuditQuery',
  get: async () => {
    const { data } = await Service.Orders.V1.getInteractionChannelAudit({ screenName });
    return data;
  },
});

export const orderInteractionChannelAuditQueryUpdated = selector({
  key: 'Order/InteractionChannelAuditQueryUpdated',
  get: async () => {
    const { data } = await Service.Orders.V1.getInteractionChannelAudit({ screenName });

    const normalizedData = normalizeInteractionChannels(data);

    return normalizedData;
  },
});
